import { forwardRef, type InputHTMLAttributes, type ReactNode, useState } from 'react';
import { cn } from '../../utils/cn';
import { borderRadius } from '../../tokens/spacing';
import { transitions } from '../../utils/motion';

type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  inputSize?: InputSize;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      inputSize = 'md',
      iconLeft,
      iconRight,
      id,
      disabled,
      readOnly,
      className,
      ...props
    },
    ref,
  ): JSX.Element => {
    const inputId = id ?? (label ? `input-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);
    const errorId = inputId ? `${inputId}-error` : undefined;
    const helperId = inputId ? `${inputId}-helper` : undefined;
    const [focused, setFocused] = useState(false);

    const sizeStyles: Record<InputSize, { padding: string; fontSize: string; height: string }> = {
      sm: { padding: iconLeft || iconRight ? '0.375rem 0.625rem' : '0.375rem 0.625rem', fontSize: '0.8125rem', height: '32px' },
      md: { padding: iconLeft || iconRight ? '0.5rem 0.75rem' : '0.5rem 0.75rem', fontSize: '0.875rem', height: '40px' },
      lg: { padding: iconLeft || iconRight ? '0.625rem 0.875rem' : '0.625rem 0.875rem', fontSize: '1rem', height: '48px' },
    };

    const currentSize = sizeStyles[inputSize];

    return (
      <div
        className={cn('ds-input-wrapper', className)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem',
          width: '100%',
          /* `flex` e `minWidth` para o campo se comportar nos DOIS contextos:
             em pai block, `width: 100%` manda e o campo preenche a linha; em
             pai flex (toolbar de filtro), `flex-basis: 0` deixa os campos
             dividirem a linha em vez de cada um reivindicar 100% e quebrar
             para a propria linha.

             Sem isso, a toolbar de /fiscal/invoices — que ja era `display:
             flex` — renderizava os tres filtros EMPILHADOS, cada um com a
             largura inteira do card. Visto na captura. */
          flex: '1 1 0%',
          minWidth: 0,
        }}
      >
        {label && (
          <label
            htmlFor={inputId}
            className="ds-input__label"
            style={{
              /* label-md do prototipo: 12px JetBrains Mono, 0.02em, 500,
                 em on-surface-variant (medido em 168 labels de campo) */
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: '12px',
              lineHeight: '16px',
              letterSpacing: '0.02em',
              fontWeight: 500,
              color: disabled ? '#74777f' : '#43474e',
            }}
          >
            {label}
          </label>
        )}

        <div
          className={cn(
            'ds-input__container',
            error && 'ds-input__container--error',
            focused && 'ds-input__container--focused',
            disabled && 'ds-input__container--disabled',
          )}
          style={{
            display: 'flex',
            alignItems: 'center',
            background: disabled ? '#f2f4f6' : readOnly ? '#f2f4f6' : '#f7f9fb',
            border: `1px solid ${error ? '#ba1a1a' : focused ? '#002045' : disabled ? '#eceef0' : '#c4c6cf'}`,
            borderRadius: borderRadius.sm,
            transition: transitions.fast,
            boxShadow: focused
              ? error
                ? '0 0 0 1px #ba1a1a'
                : '0 0 0 1px #002045'
              : 'none',
            cursor: disabled ? 'not-allowed' : readOnly ? 'default' : 'text',
            minHeight: currentSize.height,
          }}
        >
          {iconLeft && (
            <span
              className="ds-input__icon ds-input__icon--left"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                paddingLeft: iconLeft ? '0.625rem' : '0',
                color: disabled ? '#74777f' : '#43474e',
                flexShrink: 0,
                fontSize: currentSize.fontSize,
              }}
            >
              {iconLeft}
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            readOnly={readOnly}
            aria-invalid={!!error}
            aria-describedby={
              [error ? errorId : null, !error && helperText ? helperId : null]
                .filter(Boolean)
                .join(' ') || undefined
            }
            className="ds-input__field"
            style={{
              width: '100%',
              padding: currentSize.padding,
              fontSize: currentSize.fontSize,
              lineHeight: 1.5,
              color: disabled ? '#74777f' : '#191c1e',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontFamily: 'inherit',
              cursor: disabled ? 'not-allowed' : readOnly ? 'default' : 'text',
              minHeight: '100%',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              setFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              props.onBlur?.(e);
            }}
            {...props}
          />

          {iconRight && (
            <span
              className="ds-input__icon ds-input__icon--right"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                paddingRight: iconRight ? '0.625rem' : '0',
                color: disabled ? '#74777f' : '#43474e',
                flexShrink: 0,
                fontSize: currentSize.fontSize,
              }}
            >
              {iconRight}
            </span>
          )}
        </div>

        {error && (
          <p id={errorId} className="ds-input__error" role="alert" style={{ margin: 0, fontSize: '0.75rem', color: '#ba1a1a' }}>
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperId} className="ds-input__helper" style={{ margin: 0, fontSize: '0.75rem', color: '#43474e' }}>
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
